import { speak } from './TranslatableText';
import { RarityTag } from './HabitatPanel';
import { HABITAT_INFO } from '../game/habitats';
import { fishName, oceanWords } from '../game/lang';
import type { AreaId } from '../game/world';
import { RARITY, SPECIES, speciesImage, type Rarity } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// The Fishpedia: every species, greyed out until the student catches one or one
// moves into a habitat they built (ocean_fishpedia). Names are in the class's
// language, with a button to hear them; hints say where each one lives.
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  found: { species: string; how: 'caught' | 'attracted' | 'hatched' }[];
  lang: string;
  onClose: () => void;
};

const ORDER: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];

export default function Fishpedia({ found, lang, onClose }: Props) {
  const words = oceanWords(lang);
  const how = new Map(found.map(f => [f.species, f.how]));
  const placeName = (a: string) => (a === 'caves' ? words.areas.caves
    // The Abyss is hard to find: say how to get there.
    : a === 'abyss' ? `${words.areas.abyss} (the bottom of the Sunken Temple: through the deepest cave, past the temple gate, all the way down)`
    : words.areas[a as AreaId] ?? a);
  return (
    <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Fishpedia" onClick={onClose}>
      <div className="ocean-card tray wide pedia" onClick={e => e.stopPropagation()}>
        <div className="row">
          <h3>📖 Fishpedia</h3>
          <span className="small muted">{how.size} of {SPECIES.length} discovered</span>
          <span className="spacer" />
          <button type="button" className="icon" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="habitat-bar"><span style={{ width: `${(how.size / SPECIES.length) * 100}%` }} /></div>
        <div className="pedia-scroll">
          {ORDER.map(r => {
            const list = SPECIES.filter(s => s.rarity === r);
            return (
              <section key={r}>
                <h4 className="recipe-head" style={{ color: RARITY[r].color }}>{RARITY[r].name} · {list.filter(s => how.has(s.id)).length}/{list.length}</h4>
                <div className="pedia-grid">
                  {list.map(s => {
                    const got = how.get(s.id);
                    const name = fishName(s.id, s.name, lang);
                    return (
                      <div key={s.id} className={`pedia-card${got ? '' : ' unknown'}`} style={{ borderColor: got ? RARITY[r].color : undefined }}>
                        <img src={speciesImage(s.id)} alt={got ? s.name : 'Not discovered yet'} />
                        {got ? (
                          <>
                            <div className="pedia-name" dir="auto">
                              <strong>{name}</strong>
                              <button type="button" className="icon small" aria-label={`Say ${name}`} onClick={() => speak(name, lang)}>🔊</button>
                            </div>
                            {name !== s.name && <div className="small muted">{s.name}</div>}
                            <div className="small">{got === 'caught' ? '🎣 Caught' : '🏡 Moved into your habitat'} <RarityTag id={s.id} /></div>
                          </>
                        ) : <div className="pedia-name"><strong>???</strong></div>}
                        <div className="small muted" dir="auto">
                          📍 {s.areas.map(placeName).join(', ')}{s.nightOnly ? ' · 🌙 only at night' : ''}{s.night ? ` · 🌙 ${s.night.map(placeName).join(', ')}` : ''}
                        </div>
                        <div className="small muted">🏡 {s.attracts.map(t => HABITAT_INFO[t].icon).join(' ')}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
